import moment from "moment";
import {
  EAltitudeType,
  EAltitudeUnits,
  ENotamFields,
  IAftnMessage,
  INotam,
  INotamContent,
  INotamHeader,
  INotamLimits,
  INotamLimitsDetails,
  INotamQualification,
  INotamSchedule,
  INotamSimple,
  TAftnContentType,
  TNotamCoordinates,
} from "@naviair-utm/node-shared-interfaces";
import { modifiers, subjects } from "./notamConst";

export const clean = (text?: string) => {
  if (text) {
    text = text.replace(/[\r\n]+/gm, " ");
    return text.replace(/^\s+|\s+$|\s+(?=\s)/g, "");
  }
};

export const convertDMSToDD = (
  degrees: number,
  minutes: number,
  seconds: number,
  direction: string
) => {
  let dd = degrees + minutes / 60 + seconds / (60 * 60);
  if (direction == "S" || direction == "W") {
    dd = dd * -1;
  }
  return +dd.toFixed(6);
};

export const getISODateString = (str: string, format: string) => {
  let unixTimestamp = moment.utc(moment(str, format)).unix() * 1000;
  return new Date(unixTimestamp).toISOString();
};

//returns a array of coordinates in a text
export const findCoordinates = (str?: string) => {
  const text = clean(str);
  let res = [];
  if (text && text.length >= 15) {
    // ex: 402500N 075000W             (15)
    for (let index = 0; index < text.length - 15; index++) {
      if (
        (text[index + 6] == "N" || text[index + 6] == "S") &&
        (text[index + 14] == "W" || text[index + 14] == "E")
      ) {
        if (
          !isNaN(Number(text.substring(index, index + 6))) &&
          !isNaN(Number(text.substring(index + 7, index + 14)))
        ) {
          const c = getCoords(text.substring(index, index + 15));
          if (c != null) res.push(c);
        }
      }
    }
    // ex: 412644N 0083144W           (16)
    for (let index = 0; index < text.length - 16; index++) {
      if (
        (text[index + 6] == "N" || text[index + 6] == "S") &&
        (text[index + 15] == "W" || text[index + 15] == "E")
      ) {
        if (
          !isNaN(Number(text.substring(index, index + 6))) &&
          !isNaN(Number(text.substring(index + 7, index + 15)))
        ) {
          const c = getCoords(text.substring(index, index + 16));
          if (c != null) res.push(c);
        }
      }
    }
    // ex: 411415.07N 0083700.29W     (22)
    for (let i = 0; i < text.length - 22; i++) {
      if (
        (text[i + 9] == "N" || text[i + 9] == "S") &&
        (text[i + 21] == "W" || text[i + 21] == "E")
      ) {
        if (
          !isNaN(Number(text.substring(i, i + 6))) &&
          !isNaN(Number(text.substring(i + 11, i + 18))) &&
          text.substring(i + 6, i + 7) == "." &&
          text.substring(i + 18, i + 19) == "."
        ) {
          const c = getCoords(text.substring(i, i + 22));
          if (c != null) res.push(c);
        }
      }
    }
  }
  return res;
};

// returns a [lat,lng] from most common notam coordinate formats
export const getCoords = (coordinates: string): TNotamCoordinates | null => {
  if (coordinates.length === 14) {
    //ex: 4025N00404W999
    const lat = convertDMSToDD(
      +coordinates.substring(0, 2),
      +coordinates.substring(2, 4),
      0,
      coordinates.substring(4, 5)
    );
    const lng = convertDMSToDD(
      +coordinates.substring(5, 8),
      +coordinates.substring(8, 10),
      0,
      coordinates.substring(10, 11)
    );
    return { latLng: [lat, lng], radius: +coordinates.substring(11, 14) };
  } else if (coordinates.length === 15) {
    // ex: 402500N 075000W             (15)
    const lat = convertDMSToDD(
      +coordinates.substring(0, 2),
      +coordinates.substring(2, 4),
      +coordinates.substring(4, 6),
      coordinates.substring(6, 7)
    );
    const lng = convertDMSToDD(
      +coordinates.substring(8, 10),
      +coordinates.substring(10, 12),
      +coordinates.substring(12, 14),
      coordinates.substring(14, 15)
    );
    return { latLng: [lat, lng] };
  } else if (coordinates.length === 16) {
    // ex: 280944N 0152630W  (16)
    const lat = convertDMSToDD(
      +coordinates.substring(0, 2),
      +coordinates.substring(2, 4),
      +coordinates.substring(4, 5),
      coordinates.substring(5, 16)
    );
    const lng = convertDMSToDD(
      +coordinates.substring(8, 11),
      +coordinates.substring(11, 13),
      +coordinates.substring(13, 15),
      coordinates.substring(15, 16)
    );
    return { latLng: [lat, lng] };
  } else if (coordinates.length === 22) {
    // ex: 411415.07N 0083700.29W     (22)
    const lat = convertDMSToDD(
      +coordinates.substring(0, 2),
      +coordinates.substring(2, 4),
      +coordinates.substring(4, 9),
      coordinates.substring(9, 10)
    );
    const lng = convertDMSToDD(
      +coordinates.substring(11, 14),
      +coordinates.substring(14, 16),
      +coordinates.substring(16, 21),
      coordinates.substring(21, 22)
    );
    return { latLng: [lat, lng] };
  } else {
    return null;
  }
};

export const getPurpose = (purpose: string) => {
  let res = [];
  for (const c of purpose) {
    switch (c) {
      case "N":
        res.push({
          code: c,
          description:
            "Selected for the immediate attention to aircraft operators",
        });
        break;
      case "O":
        res.push({
          code: c,
          description: "Operationally significant for IFR flights",
        });
        break;
      case "B":
        res.push({
          code: c,
          description: "For inclusion in Preflight Infomation Bulletins",
        });
        break;
      case "M":
        res.push({ code: c, description: "Miscellaneous information" });
        break;
      case "K":
        res.push({ code: c, description: "Checklist" });
        break;
      default:
        break;
    }
  }
  return res;
};

export const getScope = (scope: string) => {
  let res = [];
  for (const c of scope) {
    switch (c) {
      case "A":
        res.push({ code: c, description: "Aerodrome" });
        break;
      case "E":
        res.push({ code: c, description: "En-route information" });
        break;
      case "W":
        res.push({ code: c, description: "Navigational warnings" });
        break;
      case "K":
        res.push({ code: c, description: "Checklist" });
        break;
      default:
        break;
    }
  }
  return res;
};

export const getTraffic = (traffic: string) => {
  let res = [];
  for (const c of traffic) {
    switch (c) {
      case "I":
        res.push({ code: c, description: "IFR Traffic" });
        break;
      case "V":
        res.push({ code: c, description: "VFR Traffic" });
        break;
      case "K":
        res.push({ code: c, description: "Checklist" });
        break;
      default:
        break;
    }
  }
  return res;
};

// converts raw notam to simple object
export const convertMessage = (msg: string) => {
  let mappings: Array<{ code: string; index: number }> = [];
  ["Q", "A", "B", "C", "D", "E", "F", "G"].forEach((code) => {
    if (msg.indexOf("\n" + code + ")") > -1) {
      mappings.push({ code: code, index: msg.indexOf("\n" + code + ")") });
    } else if (msg.indexOf(" " + code + ")") > -1) {
      mappings.push({ code: code, index: msg.indexOf(" " + code + ")") });
    }
  });
  if (mappings.length == 0) return;

  let res: INotamSimple = {
    header: msg.substring(0, mappings[0].index),
  };
  for (let i = 0; i < mappings.length; i++) {
    let content =
      i < mappings.length - 1
        ? msg.substring(mappings[i].index + 3, mappings[i + 1].index)
        : msg.substring(mappings[i].index + 3);
    res[mappings[i].code.toLowerCase()] = content.trim();
  }
  return res;
};

// convert header
export const parseHeader = (line: string): INotamHeader => {
  let obj: INotamHeader = {};
  let cols = line.split(" ");
  if (cols.length > 0) {
    const id = cols[0];
    obj.id = clean(id);
    if (id.length == 8 || id.length === 11) {
      // Parse default info. Series, number and year
      obj.series = id.substring(0, 1);
      obj.number = id.substring(1, 5);
      obj.year = id.substring(6, 8);
    }
    if (id.length === 11) {
      // If this is a multi-part Notam, a letter and two numbers are added to the end of the id
      obj.multiPartLetter = id.substring(8, 9);
      obj.multiPartTotal = parseInt(id.substr(9, 11));

      // Convert the letters to a number where A=1, B=2 and so on.
      // Using radix 36, 0=0, 1=1 and A=10, therefore the letter is parsed and 9 is subtracted
      obj.multiPartIndex = parseInt(obj.multiPartLetter, 36) - 9;
    }

    if (cols.length > 1) {
      const type: TAftnContentType = cols[1] as TAftnContentType;
      obj.type = type;
      if (type === "NOTAMN") {
        obj.typeDesc = "New NOTAM";
      } else if (type === "NOTAMR") {
        obj.typeDesc = "Replacement NOTAM";
        obj.idToReplace = cols[2];
      } else if (type === "NOTAMC") {
        obj.typeDesc = "Cancellation NOTAM";
        obj.idToCancel = cols[2];
      }
    }
  }
  return obj;
};

// convert Q line to Object
export const parseQualificationLine = (q?: string): INotamQualification => {
  let qualifier: INotamQualification = {
    line: q,
  };
  if (q) {
    const cols = q.split("/");
    if (cols.length == 8) {
      const location = cols[0];
      const code = cols[1].substr(1, 4);
      const subjectCategoryCode = cols[1].substr(1, 1);
      const subjectCode = cols[1].substr(1, 2);
      const modifierCategoryCode = cols[1].substr(3, 1);
      const modifierCode = cols[1].substr(3, 5);
      const traffic = cols[2];
      const purpose = cols[3];
      const scope = cols[4];
      const lower = cols[5];
      const upper = cols[6];
      const coordinates = cols[7];

      // process
      qualifier.location = location; // TODO: see 2.3.5.2 of https://www.eurocontrol.int/sites/default/files/publication/files/OPADD_Ed4.0_v01.00.pdf
      qualifier.code = {
        code: code,
      };

      if (subjectCategoryCode === "K") {
        // console.warn('Checklist', cols);
      } else {
        // subject
        qualifier.code.entity = subjectCode;
        qualifier.code.status = modifierCode;

        const subjectCategory = subjects.find(
          (x) => x.code === subjectCategoryCode
        );
        if (subjectCategory != null) {
          if (subjectCategory.area != null)
            qualifier.code.area = subjectCategory.area;
          if (subjectCategory.subArea != null)
            qualifier.code.subArea = subjectCategory.subArea;
          const subject = subjectCategory.subjects.find(
            (ca) => ca.entity === subjectCode
          );
          if (subject != null) {
            if (subject.subject != null)
              qualifier.code.subject = subject.subject;
          } else {
            console.warn("Unable to find subject with code ", subjectCode);
          }
        } else {
          console.warn(
            "Unable to find subject category with code ",
            subjectCategoryCode
          );
        }
        const modifierCategory = modifiers.find(
          (x) => x.code === modifierCategoryCode
        );
        if (modifierCategory != null) {
          if (modifierCategory.condition != null)
            qualifier.code.condition = modifierCategory.condition;
          const modifier = modifierCategory.list.find(
            (m) => m.status === modifierCode
          );
          if (modifier != null) {
            if (modifier.status != null)
              qualifier.code.status = modifier.status;
            if (modifier.modifier != null)
              qualifier.code.modifier = modifier.modifier;
          } else {
            console.warn("Unable to find modifier with code ", modifierCode);
          }
        } else {
          console.warn(
            "Unable to find modifier category with code ",
            modifierCategoryCode,
            cols
          );
        }
      }

      qualifier.traffic = getTraffic(traffic);
      qualifier.purpose = getPurpose(purpose);
      qualifier.scope = getScope(scope);
      // coordinates
      const c = getCoords(coordinates);
      if (c != null) {
        qualifier.coordinates = c;
      }
      // limits
      if (lower != null || upper != null) {
        qualifier.limits = {};
        if (lower != null)
          qualifier.limits.lower = parseLimitsDetails(`FL${lower}`);
        if (upper != null)
          qualifier.limits.upper = parseLimitsDetails(`FL${upper}`);
      }
    } else {
      console.warn("qualifier line has wrong number of fields");
    }
  }
  return qualifier;
};

export const parseSchedule = (
  b?: string,
  c?: string,
  d?: string
): INotamSchedule => {
  let res: INotamSchedule = {
    activityStart: null,
    validityEnd: null,
    elements: [],
    exceptions: [],
  };
  // activity
  if (b) {
    const bCols = b.split(" ");
    if (bCols.length > 0) {
      res.activityStart = getISODateString(bCols[0], "YYMMDDHHmm");
      if (bCols.length > 1) {
        if (bCols[1] == "EST") {
          // ‘WIE’, ‘SR’ or ‘SS’
          res.activityEstimated = true;
        }
      }
    }
  }

  // validityEnd
  if (c) {
    const cCols = c.split(" ");
    if (cCols.length > 0) {
      if (cCols[0] == "PERM") {
        res.permanent = true;
      } else {
        res.validityEnd = getISODateString(cCols[0], "YYMMDDHHmm");
      }
      if (cCols.length > 1) {
        if (cCols[1] == "EST") {
          res.estimated = true;
        }
      }
    }
  }

  if (d) {
    const data = d.split("EXC");
    const groups = data[0].split(",");
    const exceptions = data.length == 2 ? data[1].split(",") : null;

    if (groups.length > 0) {
      res.elements = [];
      groups.forEach((element) => {
        res.elements.push(clean(element));
      });
    }
    if (exceptions != null) {
      res.exceptions = [];
      exceptions.forEach((exception) => {
        res.exceptions.push(clean(exception));
      });
    }
  }

  return res;
};

export const parseContent = (e?: string) => {
  let res: INotamContent = {
    text: e,
  };

  //search for circles
  if (e && e.search("RADIUS") != -1) {
    //console.log(e);
  } else {
    const coordinates = findCoordinates(e);
    if (coordinates.length > 0) {
      res.area = coordinates;
    }
  }

  return res;
};

export const parseLimitsDetails = (input: string): INotamLimitsDetails => {
  const trimmedInput = input.trim();
  let limitDetails: INotamLimitsDetails = {};
  if (
    trimmedInput == EAltitudeType.SFC ||
    trimmedInput == EAltitudeType.GND ||
    trimmedInput == EAltitudeType.UNL
  ) {
    //If limit SFC, GND or UNL
    limitDetails.type = trimmedInput;
  } else if (
    trimmedInput.substring(0, 2) == EAltitudeUnits.FL &&
    trimmedInput.length == 5
  ) {
    //If limit is flight level
    limitDetails.units = EAltitudeUnits.FL;
    limitDetails.altitude = Number(trimmedInput.substring(2, 5));
  } else {
    const splittedInput = trimmedInput.split(" ");
    //Find type
    if (splittedInput[0].indexOf(EAltitudeUnits.M) > -1) {
      limitDetails.units = EAltitudeUnits.M;
      limitDetails.altitude = Number(
        splittedInput[0].substring(
          0,
          splittedInput[0].indexOf(EAltitudeUnits.M)
        )
      );
    } else if (splittedInput[0].indexOf(EAltitudeUnits.FT) > -1) {
      limitDetails.units = EAltitudeUnits.FT;
      limitDetails.altitude = Number(
        splittedInput[0].substring(
          0,
          splittedInput[0].indexOf(EAltitudeUnits.FT)
        )
      );
    }

    //Get altitude type
    if (
      splittedInput[1] == EAltitudeType.AGL ||
      splittedInput[1] == EAltitudeType.AMSL
    ) {
      limitDetails.type = splittedInput[1] as EAltitudeType;
    }
  }
  return limitDetails;
};

export const parseLimits = (f?: string, g?: string) => {
  // AGL or SFC or AMSL
  if (f != null || g != null) {
    let res: INotamLimits = {};
    if (f != null) {
      const cols = f.split("\n");
      res.lower = parseLimitsDetails(cols[0]);
    }
    if (g != null) {
      const cols = g.split("\n");
      res.upper = parseLimitsDetails(cols[0]);
    }
    return res;
  }
  return null;
};

/**
 * Searches for DAILY xxxx-yyyy and captures the xxxx and yyyy in two groups named "start" and "end"
 */
const notamScheduleRegex = /DAILY (?<start>\d{4})-(?<end>\d{4})/gm;

/**
 * Notam schedule states indicating whether the schedule of a notam is active right now, will get active later this day or is disabled
 */
export enum ENotamScheduleStates {
  ACTIVE = "active",
  INACTIVE = "inactive",
  DISABLED = "disabled",
  NO_SCHEDULE = "no_schedule",
}
/**
 * Check whether the current time is within the time schedule of a notam with a schedule of "DAILY xxxx-yyyy" or not.
 * If no D-field is present in the notam, it will return no_schedule. If the D-field contains information other than the format "DAILY xxxx-yyyy" it will also return no_schedule.
 * If a schedule is present, the function will return whether the notam is active, inactive or disabled
 * @param notam Notam message @see IAftnMessage
 * @returns boolean whether the schedule is active or not
 */
export const notamScheduleStatus = (
  notam: IAftnMessage<INotam>
): ENotamScheduleStates => {
  const notamSchedule = parseNotamSchedule(notam);

  if (notamSchedule !== undefined && typeof notamSchedule === "object") {
    // Get current timestamp in UTC
    const now = moment.utc();

    // Check if the schedule is across days or the same day (if the end time is before the start time, it must be the next day)
    if (notamSchedule.end.isBefore(notamSchedule.start)) {
      // The end time is actually the next day
      if (now.isSameOrAfter(notamSchedule.start) || now.isBefore(notamSchedule.end)) {
        // Now is either AFTER the start or BEFORE the end (which is enough to check for if the end is the day after the start)
        return ENotamScheduleStates.ACTIVE;
      } else {
        // As the schedule is across days, the notam will inevitably get active this day
        return ENotamScheduleStates.INACTIVE;
      }
    } else {
      // The end time is in the same day as the start
      if (now.isBetween(notamSchedule.start, notamSchedule.end, 'minute', '[]')) {
        // Now is between the start and end time
        return ENotamScheduleStates.ACTIVE;
      }
       else if (now.isSameOrBefore(notamSchedule.start)) {
        // Now if before the start, therefore inactive (but active later this day)
        return ENotamScheduleStates.INACTIVE;
      } else if (now.isSameOrAfter(notamSchedule.end)) {
        // Now is past the end time, therefore disabled until next day
        return ENotamScheduleStates.DISABLED;
      }
    }    
  }

  // If no D field, no schedule or any case where this function has not returned anything yet, no schedule is present
  return ENotamScheduleStates.NO_SCHEDULE;
};

export interface INotamRepeatSchedule {
  start: moment.Moment;
  end: moment.Moment;
  frequency: string; // For now only possible value is "DAILY"
}

/**
 * Parse the repeat schedule of a notam. Will look ik rawFields.d for a schedule in the format "DAILY xxxx-yyyy" and will return 'undefined' if not matched.
 * @param notam A parsed notam message @see IAftnMessage
 * @returns @see INotamRepeatSchedule or undefined if not found
 */
export const parseNotamSchedule = (
  notam: IAftnMessage<INotam>
): INotamRepeatSchedule | undefined => {
  const schedule = notamScheduleRegex.exec(
    notam.parsedMessage.rawFields[ENotamFields.d]
  );
  //! Important to "reset" the regex. Will return "null" at next attempt if not reset this way
  notamScheduleRegex.lastIndex = 0;

  // .exec() returns null if not matched
  if (schedule !== null) {
    // Get current timestamp in UTC for generating the year, month and date
    const now = moment.utc();

    // Set const to the numeric value of the hour and minute of start and end
    const startHour: number = parseInt(schedule.groups.start.substring(0, 2), 10);
    const startMinute: number = parseInt(schedule.groups.start.substring(2, 4), 10);
    const endHour: number = parseInt(schedule.groups.end.substring(0, 2), 10);
    const endMinute: number = parseInt(schedule.groups.end.substring(2, 4), 10);

    // Generate a Moment UTC timestamp for the start hour and minute THIS day (even if in the past)
    const start = moment.utc([
      now.year(),
      now.month(),
      now.date(),
      startHour,
      startMinute,
    ]);

    // Generate a Moment UTC timestamp for the end hour and minute THIS day (even if in the past)
    const end = moment.utc([
      now.year(),
      now.month(),
      now.date(),
      endHour,
      endMinute,
    ]);

    return {
      start: start,
      end: end,
      frequency: "DAILY",
    } as INotamRepeatSchedule;
  }

  return undefined;
};
